/**
 * Status Update Conditions section for the SFTP Push tab. Each card is one rule:
 * when its conditions match, the row's `sftp_status` is set to the rule's result
 * (success / resubmit). Rules are evaluated in order. Each card has a result
 * selector, an optional comment, a QueryBuilder, and a delete; "Add condition
 * group" appends a blank rule.
 */
import { Button, Select, TextInput } from "risa-oasis-ui_v2";
import QueryBuilder from "../../../../../components/queryBuilder/QueryBuilder";
import { SFTP_RESULT_OPTIONS } from "../../../../../data-model/nycbsSftpPushConfig";
import { QueryBuilderConfig, QueryGroup } from "../../../../../data-model/queryBuilder";
import { AddIcon } from "../../../../../svg/add-icon";
import { Delete } from "../../../../../svg/delete";
import { StatusUpdateGroupForm } from "./useNycbsSftpPushConfig";

interface StatusUpdateConditionsSectionProps {
  resetKey: number;
  groups: StatusUpdateGroupForm[];
  onChange: (groups: StatusUpdateGroupForm[]) => void;
  onAdd: () => void;
  config: QueryBuilderConfig;
}

const StatusUpdateConditionsSection = ({
  resetKey,
  groups,
  onChange,
  onAdd,
  config,
}: StatusUpdateConditionsSectionProps) => {
  const patchGroup = (id: string, patch: Partial<StatusUpdateGroupForm>) =>
    onChange(groups.map((g) => (g.id === id ? { ...g, ...patch } : g)));

  const removeGroup = (id: string) =>
    onChange(groups.filter((g) => g.id !== id));

  return (
    <div className="flex flex-col gap-3 rounded-lg border border-primaryGray-16 p-4">
      <div className="flex items-center justify-between">
        <div className="text-sm font-semibold text-primaryGray-1">
          Status Update Conditions
        </div>
        <Button
          buttonType="primary"
          size="medium"
          disabled={false}
          onClick={onAdd}
        >
          <AddIcon width="14" height="14" color="#ffffff" />
          Add condition group
        </Button>
      </div>
      <div className="text-tiny text-primaryGray-8">
        Rules are checked in order; the first matching rule sets the row&apos;s
        resubmit result.
      </div>

      {groups.length === 0 ? (
        <div className="text-tiny text-primaryGray-12">
          No rules configured.
        </div>
      ) : (
        groups.map((group) => (
          <div
            key={group.id}
            className="flex flex-col gap-3 rounded-md border border-primaryGray-15 p-3"
          >
            <div className="flex flex-row items-end gap-2">
              <div className="w-44">
                <Select
                  id={`sftp-result-${group.id}`}
                  label="Result"
                  options={SFTP_RESULT_OPTIONS}
                  value={group.result}
                  defaultValue={group.result}
                  placeholder="Select result"
                  onOptionChange={(data: { value: string }) =>
                    patchGroup(group.id, { result: data.value })
                  }
                />
              </div>
              <div className="flex-1">
                <TextInput
                  id={`sftp-comment-${group.id}`}
                  label="Comment (optional)"
                  placeholder="Why this rule exists"
                  defaultValue={group._comment ?? ""}
                  onChange={(data: { value?: string }) =>
                    patchGroup(group.id, { _comment: data.value ?? "" })
                  }
                  onBlur={(data: { value?: string }) =>
                    patchGroup(group.id, { _comment: data.value ?? "" })
                  }
                />
              </div>
              <div
                className="mb-2 cursor-pointer"
                onClick={() => removeGroup(group.id)}
              >
                <Delete width={16} height={16} />
              </div>
            </div>
            <QueryBuilder
              key={`status-update-${group.id}-${resetKey}`}
              value={group.query}
              onChange={(query: QueryGroup) => patchGroup(group.id, { query })}
              config={config}
            />
          </div>
        ))
      )}
    </div>
  );
};

export default StatusUpdateConditionsSection;
